/**
 * Caller principal type as recorded by ARM in the `systemData` block.
 *
 * - `User`: interactive AAD user; `createdBy` / `lastModifiedBy` are typically the UPN (email).
 * - `Application`: AAD application / service principal; the *By field is the app id (GUID-shaped).
 * - `ManagedIdentity`: managed identity principal; the *By field is the MI's id.
 * - `Key`: legacy shared-key auth; the *By field is the key name.
 *
 * Older entities may have `systemData` present without these sub-fields populated.
 */
export type SystemDataPrincipalType = 'User' | 'Application' | 'ManagedIdentity' | 'Key';

export interface SystemData {
    // Individual sub-fields are nullable on legacy entities (created before the ARM
    // capture work shipped)  keep `null` in every type so consumers do explicit checks
    // instead of relying on truthiness outside the type system.
    createdBy?: string | null;
    createdByType?: SystemDataPrincipalType | string | null;
    createdAt?: string | null;
    lastModifiedBy?: string | null;
    lastModifiedByType?: SystemDataPrincipalType | string | null;
    lastModifiedAt?: string | null;
}

/** Per-user-assigned-identity entry inside `identity.userAssignedIdentities`. */
export interface UserAssignedIdentityValue {
    principalId?: string;
    clientId?: string;
}

/**
 * ARM managed-identity block on an automation application.
 *
 * `type` values follow the ARM convention: `None`, `SystemAssigned`, `UserAssigned`,
 * or the combined `SystemAssigned,UserAssigned`. The principal/tenant ids are only
 * populated when SystemAssigned is in play; the user-assigned dictionary is only
 * populated when UserAssigned is in play.
 */
export interface AutomationIdentity {
    type: 'None' | 'SystemAssigned' | 'UserAssigned' | 'SystemAssigned,UserAssigned' | string;
    principalId?: string;
    tenantId?: string;
    userAssignedIdentities?: Record<string, UserAssignedIdentityValue>;
}

export interface AutomationProject {
    id: string;
    name: string;
    location: string;
    type: string;
    /**
     * True when the project is provisioned under the free grant SKU. Surfaced by
     * the existing project GET/LIST. May appear top-level on some responses; the
     * canonical location is inside `properties`. Use `getIsFreeProject()` to read
     * it defensively regardless of placement.
     */
    isFreeProject?: boolean;
    properties: {
        provisioningState: string;
        description: string;
        /** True when the project is a free-grant project. See `getIsFreeProject()`. */
        isFreeProject?: boolean;
    };
    systemData?: SystemData;
}

/**
 * Returns whether a project is a free-grant project, tolerant of the flag
 * living either at the resource top level or inside `properties`.
 */
export const getIsFreeProject = (
    project: Pick<AutomationProject, 'isFreeProject' | 'properties'> | null | undefined,
): boolean => project?.properties?.isFreeProject === true || project?.isFreeProject === true;

/**
 * A single free-grant meter balance for an automation project, returned by
 * `GET .../automationProjects/{project}/freeGrantBalances`. All quantities are
 * expressed in the meter's own unit; `limit = used + remaining`.
 */
export interface FreeGrantBalance {
    /** Human-readable meter name, e.g. "Automation Core Execution". */
    meter: string;
    /** How often the grant resets, e.g. "Monthly" or "None" (one-time). */
    resetCadence: string;
    /** Total granted quantity for the current period. */
    limit: number;
    /** Quantity still available. */
    remaining: number;
    /** Quantity already consumed. */
    used: number;
}

/** Live running status of an application's underlying Container App. */
export type RunningStatus = 'Running' | 'Stopped' | 'Stopping' | 'Starting' | 'Unknown';

export interface AutomationApp {
    /** ARM resource id (full path). Populated on single GET and LIST since the enrichment work. */
    id?: string;
    name: string;
    type: 'applications';
    /** Azure region of the resource. */
    location?: string;
    /** ARM "managed by" link; usually null unless the resource is owned by a parent group. */
    managedBy?: string | null;
    /** ARM ETag for concurrency. Always present on enriched responses. */
    etag?: string;
    /** ARM tags dictionary; may be absent / empty. */
    tags?: Record<string, string>;
    /** Managed identity block; always present (defaults to `{ type: 'None' }`). */
    identity?: AutomationIdentity;
    /**
     * ARM-standard systemData. Always present on enriched GET; individual sub-fields may
     * be null on legacy entities created before the capture work shipped.
     */
    systemData?: SystemData;
    properties: {
        applicationId: string;
        appResourceId: string;
        /**
         * Creator's AAD object id (raw GUID). NOT human-readable on its own  resolve via
         * Microsoft Graph (`useGraphPrincipals`) before rendering. Also used as-is for
         * ownership comparisons in `useAppPermission`.
         *
         * Note: `systemData.createdBy` is an email/UPN for projects but is the same raw GUID
         * (or the legacy nullable variant) for apps, so prefer this field as the canonical
         * principal id for application surfaces.
         */
        createdBy: string;
        createdOn: string;
        /**
         * Last modifier's AAD object id (raw GUID); see `createdBy` note above for the
         * Graph-resolution and project-vs-app reasoning.
         */
        updatedBy: string;
        updatedOn: string;
        provisioningState?: string;
        /** Live running status from the underlying Container App. May be null during provisioning. */
        runningStatus?: RunningStatus | null;
        /**
         * FQDN of the underlying Container App ingress. Populated on single GET only —
         * LIST never enriches this. Null when the ARM call fails or ingress is not configured.
         */
        fqdn?: string | null;
    };
}

/** Response from POST .../start or .../stop. */
export interface StartStopResponse {
    status: string;
}

/**
 * Scale configuration for an application's Container App.
 *
 * Note: the Container App's autoscale `rules` are system-managed (custom KEDA
 * scalers provisioned by the control plane) and are intentionally NOT part of
 * this contract — they are neither returned nor settable through the scale API.
 */
export interface ScaleConfiguration {
    minReplicas: number;
    maxReplicas: number;
    /** Cooldown period in seconds before scaling back down (ACA default: 300). */
    cooldownPeriod: number;
    /** Polling interval in seconds for the scaler to evaluate (ACA default: 30). */
    pollingInterval: number;
}

/** A single Container App revision. */
export interface Revision {
    name: string;
    active: boolean;
    createdTime: string;
    image: string;
    runtimeVersion: string;
    trafficWeight: number;
    replicas: {
        running: number;
        ready: number;
    };
    healthState: string;
    provisioningState: string;
}

/** Response from GET .../revisions. */
export interface RevisionsResponse {
    value: Revision[];
}

/** A single image version entry from the image tags API. */
export interface ImageVersion {
    version: string;
    displayVersion: string;
    current: boolean;
}

/** Response from GET .../imageTags. */
export interface ImageTagsResponse {
    registry: string;
    repository: string;
    currentTag: string;
    versions: ImageVersion[];
}

/** Request body for POST .../setImage. */
export interface SetImageRequest {
    image: string;
}

/** Response from POST .../setImage. */
export interface SetImageResponse {
    previousImage: string;
    targetImage: string;
    provisioningState: string;
}

export interface AutomationAppUIModel extends AutomationApp {
    projectName: string;
    armResourceId?: string;
    fqdn?: string;
    location?: string;
}

export interface AzureContainerApp {
    id: string;
    name: string;
    location: string;
    properties: {
        provisioningState: string;
        configuration: {
            ingress: {
                fqdn: string;
            };
        };
    }
}